import { useMutation } from '@apollo/client';
import EditActionButton from '@components/EditActionButton';
import FormActionButtons from '@components/FormActionButtons';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { GET_TALENT, UPDATE_ADMINISTRATIVE_TALENT } from '@gql/talents';
import { Box, Stack, TextField } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { ExpandableText } from './ExpandableText';
import { SectionCard } from './SectionCard';
import { SectionTitle } from './SectionTitle';

interface TalentBioCardProps {
    talent: any;
    onUpdated?: () => void;
}

const TalentBioCard = ({ talent, onUpdated }: TalentBioCardProps) => {
    const { showSnackbar } = useSnackbar();
    const [isEditMode, setIsEditMode] = useState(false);

    const initialBio = useMemo(() => talent?.bio || '', [talent?.bio]);
    const [bio, setBio] = useState(initialBio);

    const [updateAdministrativeTalent, { loading }] = useMutation(UPDATE_ADMINISTRATIVE_TALENT);

    useEffect(() => {
        if (!isEditMode) {
            setBio(initialBio);
        }
    }, [initialBio, isEditMode]);

    const handleSave = async () => {
        try {
            await updateAdministrativeTalent({
                variables: {
                    talentId: talent?._id,
                    payload: {
                        firstName: talent?.firstName || '',
                        lastName: talent?.lastName || '',
                        gender: talent?.gender || '',
                        phoneNumber: talent?.phoneNumber || '',
                        preferences: talent?.preferences || [],
                        professions: talent?.professions || [],
                        bio,
                        city: talent?.city || '',
                        postalCode: talent?.postalCode || '',
                        country: talent?.country || 'France',
                        principalProfession: talent?.principalProfession,
                        address: talent?.address || '',
                        social: {
                            instagram: talent?.social?.instagram || '',
                            linkedin: talent?.social?.linkedin || '',
                            website: talent?.social?.website || '',
                        },
                        currentSalary: talent?.currentSalary,
                        expectedSalary: talent?.expectedSalary,
                        countryCode: talent?.countryCode,
                        photo: talent?.photo,
                        logo: talent?.logo,
                    },
                },
                refetchQueries: [
                    {
                        query: GET_TALENT,
                        variables: { talentId: talent?._id },
                    },
                ],
            });

            showSnackbar('Bio mise a jour avec succes', 'success');
            setIsEditMode(false);
            onUpdated?.();
        } catch (_error) {
            showSnackbar('Erreur lors de la mise a jour de la bio', 'error');
        }
    };

    return (
        <SectionCard sx={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <SectionTitle title="Bio" withInfo />
                {!isEditMode && <EditActionButton onClick={() => setIsEditMode(true)} />}
            </Box>

            {isEditMode ? (
                <Stack component="form" spacing={1.25}>
                    <TextField
                        value={bio}
                        onChange={event => setBio(event.target.value)}
                        multiline
                        minRows={4}
                        fullWidth
                        disabled={loading}
                        placeholder="Renseignez la biographie du talent"
                    />
                    <FormActionButtons
                        onCancel={() => {
                            setBio(initialBio);
                            setIsEditMode(false);
                        }}
                        onSubmit={handleSave}
                        submitType="button"
                        loading={loading}
                        submitDisabled={loading || bio === initialBio}
                        size="small"
                    />
                </Stack>
            ) : (
                <ExpandableText
                    content={initialBio || 'Aucune information de biographie disponible'}
                    maxLine={4}
                />
            )}
        </SectionCard>
    );
};

export default TalentBioCard;
